import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { shareUrlNatively } from '../../app/nativeBridge'

export interface ShareButtonProps {
  url: string
  title: string
}

type Shared = 'idle' | 'copied' | 'failed'

/**
 * Поделиться товаром. В нашем Flutter-приложении — системное окно телефона
 * через мост, в браузере — Web Share, где его нет — ссылка в буфер обмена с
 * короткой подписью «Ссылка скопирована».
 *
 * Стоит справа в строке «назад — путь по каталогу», подпись всплывает слева
 * от кнопки, не сдвигая строку.
 */
export function ShareButton({ url, title }: ShareButtonProps) {
  const { t } = useTranslation('catalog')
  const [shared, setShared] = useState<Shared>('idle')

  useEffect(() => {
    if (shared === 'idle') return
    const timer = setTimeout(() => {
      setShared('idle')
    }, 2000)
    return () => {
      clearTimeout(timer)
    }
  }, [shared])

  async function share() {
    if (await shareUrlNatively(url)) return
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url })
        return
      } catch (error) {
        // Закрыли окно «Поделиться» — это не сбой, копировать не нужно.
        if (error instanceof DOMException && error.name === 'AbortError') return
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      setShared('copied')
    } catch {
      setShared('failed')
    }
  }

  return (
    <div className="pp-share-wrap">
      {shared !== 'idle' ? (
        <span className="pp-shared" role="status">
          {t(shared === 'copied' ? 'share.copied' : 'share.failed')}
        </span>
      ) : null}
      <Press
        squish="icon"
        className="pp-share"
        aria-label={t('share.label')}
        onPress={() => {
          void share()
        }}
      >
        <Icon name="i-share" size={20} />
      </Press>
    </div>
  )
}
